import { useEffect, useState } from "react";
import api from "@/lib/api";
import { money, fmtDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import DatePicker from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScanLine, CheckCircle2, Clock, XCircle, ShieldAlert, Copy, AlertTriangle } from "lucide-react";

export default function WiPayReconciliation() {
  const [range, setRange] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/wipay/reconciliation", { params: range }).then((r) => setD(r.data)); }, [range]);
  if (!d) return <p className="text-muted-foreground">Loading…</p>;
  const s = d.summary;

  const cards = [
    { label: "Matched", value: s.matched, sub: money(s.matched_amount), icon: CheckCircle2, color: "text-emerald-500" },
    { label: "Pending", value: s.pending, icon: Clock, color: "text-amber-500" },
    { label: "Failed", value: s.failed, icon: XCircle, color: "text-rose-500" },
    { label: "Invalid Signature", value: s.invalid, icon: ShieldAlert, color: "text-rose-500" },
    { label: "Duplicates", value: s.duplicates, icon: Copy, color: "text-orange-500" },
    { label: "Anomalies", value: s.anomalies + s.unmatched_invoices, icon: AlertTriangle, color: "text-orange-500" },
  ];

  const rows = (arr, cols) => (
    <Table>
      <TableHeader><TableRow>{cols.map((c) => <TableHead key={c} className={c === "Amount" ? "text-right" : ""}>{c}</TableHead>)}</TableRow></TableHeader>
      <TableBody>
        {arr.length === 0 && <TableRow><TableCell colSpan={cols.length} className="text-center text-muted-foreground py-6">None</TableCell></TableRow>}
        {arr.map((r, i) => (
          <TableRow key={i}>
            <TableCell className="mono text-xs">{r.wipay_transaction_id || r.order_id?.slice(0, 10) || r.invoice_number}</TableCell>
            <TableCell className="text-xs">{r.invoice_number || "-"}</TableCell>
            <TableCell className="text-xs">{r.cashier || "-"}</TableCell>
            <TableCell className="text-xs">{r.issue || r.status || "-"}</TableCell>
            <TableCell className="text-right mono">{money(r.total ?? 0)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
  const C = ["Txn / Order", "Invoice", "Cashier", "Status", "Amount"];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-head text-3xl font-extrabold flex items-center gap-2"><ScanLine className="w-7 h-7 text-blue-500" /> WiPay Reconciliation</h1>
          <p className="text-muted-foreground">Match NEEC POS sales to WiPay transaction references. Spot unmatched, failed, pending or duplicate payments.</p>
        </div>
        <div className="flex gap-2 items-end">
          <div><Label className="text-xs">From</Label><DatePicker value={range.start} onChange={(v) => setRange({ ...range, start: v })} data-testid="recon-start" /></div>
          <div><Label className="text-xs">To</Label><DatePicker value={range.end} onChange={(v) => setRange({ ...range, end: v })} data-testid="recon-end" /></div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        {cards.map((c) => (
          <Card key={c.label} className="p-4" data-testid={`recon-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <c.icon className={`w-5 h-5 ${c.color}`} />
            <p className="mono text-2xl font-bold mt-2">{c.value}</p>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{c.label}</p>
            {c.sub && <p className="text-xs mono text-emerald-500">{c.sub}</p>}
          </Card>
        ))}
      </div>

      <Card className="p-0 overflow-hidden">
        <Tabs defaultValue="matched">
          <div className="p-3 border-b border-border overflow-x-auto">
            <TabsList>
              <TabsTrigger value="matched" data-testid="tab-matched">Matched ({s.matched})</TabsTrigger>
              <TabsTrigger value="pending" data-testid="tab-pending">Pending ({s.pending})</TabsTrigger>
              <TabsTrigger value="failed" data-testid="tab-failed">Failed ({s.failed})</TabsTrigger>
              <TabsTrigger value="invalid" data-testid="tab-invalid">Invalid ({s.invalid})</TabsTrigger>
              <TabsTrigger value="duplicates" data-testid="tab-duplicates">Duplicates ({s.duplicates})</TabsTrigger>
              <TabsTrigger value="anomalies" data-testid="tab-anomalies">Anomalies ({s.anomalies + s.unmatched_invoices})</TabsTrigger>
            </TabsList>
          </div>
          <TabsContent value="matched" className="m-0">{rows(d.matched, C)}</TabsContent>
          <TabsContent value="pending" className="m-0">{rows(d.pending, C)}</TabsContent>
          <TabsContent value="failed" className="m-0">{rows(d.failed, C)}</TabsContent>
          <TabsContent value="invalid" className="m-0">{rows(d.invalid, C)}</TabsContent>
          <TabsContent value="duplicates" className="m-0">{rows(d.duplicates, C)}</TabsContent>
          <TabsContent value="anomalies" className="m-0">{rows([...d.anomalies, ...d.unmatched_invoices], C)}</TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
